import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook for paginated API data fetching with "Load More" support.
 * @param {string} endpoint - The API endpoint path (can include {orgId} placeholder)
 * @param {object} options - Configuration options
 * @param {number} options.initialLimit - Items per page (default: 25)
 * @param {boolean} options.orgAsQueryParam - If true, adds org_id as query param instead of path (default: false)
 */
export function usePagination(endpoint, options = {}) {
  const { orgId, getAuthHeaders } = useAuth();
  const { initialLimit = 25, orgAsQueryParam = false } = options;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: initialLimit,
    total: 0,
    totalPages: 0
  });

  const fetchData = useCallback(async (page = 1, append = false) => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Replace {orgId} placeholder if present in endpoint
      const resolvedEndpoint = endpoint.replace('{orgId}', orgId);
      
      let url;
      // For endpoints with orgAsQueryParam=true (like /quotes/, /invoices/, /jobs/),
      // we need trailing slash because FastAPI has redirect_slashes=False
      // For endpoints with orgId in path (like /finance/{orgId}/cashflow), no trailing slash needed
      if (orgAsQueryParam) {
        const normalizedEndpoint = resolvedEndpoint.endsWith('/') ? resolvedEndpoint : `${resolvedEndpoint}/`;
        url = `${API_URL}/api/${normalizedEndpoint}?org_id=${orgId}&page=${page}&limit=${initialLimit}`;
      } else {
        url = `${API_URL}/api/${resolvedEndpoint}?page=${page}&limit=${initialLimit}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please log in to view this data');
        }
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      
      // Handle paginated response format
      const items = result.data || result;
      const paginationInfo = result.pagination || {
        page: page,
        limit: initialLimit,
        total: items.length,
        totalPages: 1
      };

      if (append) {
        setData(prev => [...prev, ...items]);
      } else {
        setData(items);
      }

      setPagination(paginationInfo);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [orgId, endpoint, initialLimit, orgAsQueryParam, getAuthHeaders]);

  const loadMore = useCallback(() => {
    if (pagination.page < pagination.totalPages && !loadingMore) {
      fetchData(pagination.page + 1, true);
    }
  }, [pagination.page, pagination.totalPages, loadingMore, fetchData]);

  const refresh = useCallback(() => {
    fetchData(1, false);
  }, [fetchData]);

  const hasMore = pagination.page < pagination.totalPages;

  return {
    data,
    loading,
    loadingMore,
    error,
    pagination,
    hasMore,
    loadMore,
    refresh,
    fetchData
  };
}

export default usePagination;
